import dayjs from 'dayjs';
import { get } from 'utils/request';
import axios from 'axios';
import { StatisticsResponse } from './statistics';
import { getRefreshDataHeader } from '../utils/getRefreshDataHeader';
import { capitalizeFirstChar } from '../utils/converter';

export interface AssetInfo {
  assetTypeCode: string;
  name: string;
  number: string;
  sectionLabel: string;
  surveyDate: string;
  installationDate: string;
  lastUpdatedDate: string;
  startChainage: string;
  endChainage: string;
  xsp: string;
}
export interface AssetInspectionInfo {
  readonly inspectionType: string;
  frequencyName: string;
  frequency: number;
  overrideNextInspectionDate: string;
  overriddenReason: string;
  overdue: boolean | string;
  lastInspectionDate: string;
  nextInspectionDate: string;
  assetId?: number;
}

export interface AssetInspectionRecord {
  id: number | string;
  inspectionDate: string;
  inspectionType: string;
  inspector: string;
  comments: string;
  signedOff: boolean | string;
}

interface Observation {
  id: number;
  defect: string;
  conditionCode: string;
  conditionScore: number;
  workCost: number;
  comments: string;
  priority: string;
  action: string;
  suggestedTreatment: string | null;
}

export interface AssetInspectionReport {
  id: number;
  inspectionType: string;
  inspectionDate: string;
  totalCosts: number;
  averageScore: number;
  observations: Observation[];
}

export interface AssetAttributesInfo {
  name: string;
  value: string;
}

export interface AssetPI {
  assetId: number;
  asset: string;
  runDate: string;
  piAverage: number;
  piCritical: number;
}

export interface AssetTypeAttributesInfo {
  group: string;
  attributeId: number;
  name: string;
  dataType: string;
  hasLookups: boolean;
  lookupValues: string[];
  id: number;
}

export interface AssetElementsInfo {
  id: number;
  name: string;
  spanNumber: number;
  assetId: number;
  assetNumber: string;
  default: boolean;
}

export interface AssetElementsRangeInfo {
  name: string;
  minValue: number;
  maxValue: number;
}

export interface EyeViDto {
  year: number;
  assetType: string;
}

export interface EyeViCredentialsDto {
  userName: number;
  password: string;
}

const EYEVI_URL = `${process.env.REACT_APP_EYEVI_URL}/api/index.php?year=uk`;

const assetActions = () => {
  const getEyeViYears = async (
    refreshData: boolean = false,
  ): Promise<EyeViDto[]> => {
    const response = await get<EyeViDto[]>(
      'config/eyevi/years',
      getRefreshDataHeader(refreshData),
    );
    return response.data;
  };

  const getEyeViCredentials = async (
    assetType: string,
    year: number,
  ): Promise<Boolean> => {
    const response = await get<EyeViCredentialsDto>(
      `config/eyevi/cred/${assetType}`,
    );
    const formatedYear = assetType === 'FW' ? `${year}footway` : `${year}`;
    const authResponse = await axios.post<any>(
      `${EYEVI_URL}${formatedYear}&request=login`,
      {
        username: response.data.userName,
        password: response.data.password,
      },
      {
        headers: {
          'Content-Type': 'application/json;charset=UTF-8',
        },
      },
    );

    if (authResponse.data.authorized) {
      // domain=eyevi.xais.co.uk;secure; samesite=none
      document.cookie = `PHPSESSID=${authResponse.data.PHPSESSID}; path=/; domain=xais.co.uk`;
      return true;
    }
    return false;
  };

  const getAssetDetails = async (
    id: number,
    refreshData: boolean = false,
  ): Promise<AssetInfo> => {
    const response = await get<AssetInfo>(
      `assets/${id}`,
      getRefreshDataHeader(refreshData),
    );
    const responseData = response.data;

    if (responseData.surveyDate) {
      responseData.surveyDate = dayjs(responseData.surveyDate).format(
        'YYYY-MM-DD',
      );
    }
    if (responseData.installationDate) {
      responseData.installationDate = dayjs(
        responseData.installationDate,
      ).format('YYYY-MM-DD');
    }
    if (responseData.lastUpdatedDate) {
      responseData.lastUpdatedDate = dayjs(responseData.lastUpdatedDate).format(
        'YYYY-MM-DD',
      );
    }

    (Object.keys(responseData) as (keyof AssetInfo)[]).forEach((key) => {
      if (responseData[key] === null || responseData[key] === '?') {
        responseData[key] = 'N/A';
      }
    });

    return responseData;
  };

  const getAssetId = async (
    id: number,
    refreshData: boolean = false,
  ): Promise<AssetInspectionInfo[]> => {
    const response = await get<AssetInspectionInfo[]>(
      `assets/${id}/inspections/dates`,
      getRefreshDataHeader(refreshData),
    );
    const responseData = response?.data.map((item: AssetInspectionInfo) => ({
      ...item,
      assetId: id,
    }));
    return responseData;
  };

  const getAssetInspectionDetails = async (
    id: number,
    refreshData: boolean = false,
  ): Promise<AssetInspectionInfo[]> => {
    const response = await get<AssetInspectionInfo[] | AssetInspectionInfo>(
      `assets/${id}/inspections/dates`,
      getRefreshDataHeader(refreshData),
    );
    const responseData = Array.isArray(response.data)
      ? response.data
      : [response.data];

    responseData.forEach((item) => {
      if (item.lastInspectionDate) {
        item.lastInspectionDate = dayjs(item.lastInspectionDate).format(
          'YYYY-MM-DD',
        );
      }
      if (item.nextInspectionDate) {
        item.nextInspectionDate = dayjs(item.nextInspectionDate).format(
          'YYYY-MM-DD',
        );
      }
      if (item.overrideNextInspectionDate) {
        item.overrideNextInspectionDate = dayjs(
          item.overrideNextInspectionDate,
        ).format('YYYY-MM-DD');
      }
      if (item.overdue !== undefined && item.overdue !== null) {
        item.overdue = capitalizeFirstChar(String(item.overdue));
      }

      const dynamic = item as unknown as Record<string, unknown>;
      for (const key of Object.keys(dynamic)) {
        if (dynamic[key] === null) {
          dynamic[key] = 'N/A';
        }
      }
    });

    return responseData;
  };

  const getInspectionRecords = async (
    assetId: number,
    refreshData: boolean = false,
  ): Promise<AssetInspectionRecord[]> => {
    const response = await get<AssetInspectionRecord[]>(
      `assets/${assetId}/inspections/records`,
      getRefreshDataHeader(refreshData),
    );
    const responseData = response.data;

    responseData.forEach((record) => {
      if (record.inspectionDate) {
        record.inspectionDate = dayjs(record.inspectionDate).format(
          'YYYY-MM-DD',
        );
      }
      if (record.signedOff) {
        record.signedOff = capitalizeFirstChar(String(record.signedOff));
      }
    });

    responseData.forEach((record) => {
      const dynamic = record as unknown as Record<string, unknown>;
      for (const key of Object.keys(dynamic)) {
        if (dynamic[key] === null) {
          dynamic[key] = 'N/A';
        }
      }
    });

    return responseData;
  };

  const getInspectionReport = async (
    assetId: number,
    inspectionid: number,
    refreshData: boolean = false,
  ): Promise<AssetInspectionReport[]> => {
    const response = await get<AssetInspectionReport[]>(
      `assets/${assetId}/inspections/records/${inspectionid}/report`,
      getRefreshDataHeader(refreshData),
    );
    const responseData = response.data;
    return responseData;
  };

  const getAssetAttributes = async (
    id: number,
    refreshData: boolean = false,
  ): Promise<AssetAttributesInfo[]> => {
    const response = await get<AssetAttributesInfo[]>(
      `assets/${id}/attributes`,
      getRefreshDataHeader(refreshData),
    );
    const responseData = response.data;
    return responseData;
  };

  const getAssetPerformanceIndicator = async (
    assetId: number,
    refreshData: boolean = false,
  ): Promise<AssetPI | null> => {
    const response = await get<AssetPI>(
      `assets/${assetId}/bci`,
      getRefreshDataHeader(refreshData),
    );

    const responseData = response.data;
    if (responseData) {
      responseData.piAverage = responseData.piAverage
        ? parseFloat(responseData.piAverage.toFixed(2))
        : 0;

      responseData.piCritical = responseData.piCritical
        ? parseFloat(responseData.piCritical.toFixed(2))
        : 0;

      return responseData;
    }

    return null;
  };

  const getAssetElements = async (
    id: number,
    refreshData: boolean = false,
  ): Promise<AssetElementsInfo[]> => {
    const response = await get<AssetElementsInfo[]>(
      `assets/${id}/elements`,
      getRefreshDataHeader(refreshData),
    );
    const responseData = response.data;
    return responseData;
  };

  const getSpans = async (
    id: number,
    refreshData: boolean = false,
  ): Promise<number> => {
    const response = await get<number>(
      `assets/${id}/spans`,
      getRefreshDataHeader(refreshData),
    );
    const responseData = response.data;
    return responseData;
  };

  const getNetworkAttributes = async (
    attributeName: string,
    refreshData: boolean = false,
  ): Promise<any> => {
    const response = await get<any>(
      `visualiser/rules/attributes/networks?attributeName=${attributeName}`,
      getRefreshDataHeader(refreshData),
    );
    const responseData = response.data;
    return responseData;
  };

  const getAssetTypeAttributes = async (
    category: string,
    type: string,
    itemKey: string,
    parameterId?: number,
    refreshData: boolean = false,
  ): Promise<AssetTypeAttributesInfo[]> => {
    if (category.toLocaleLowerCase() === 'performance') {
      const response = await get<AssetTypeAttributesInfo>(
        `visualiser/rules/attributes/performance/${itemKey.split('+')[1]}/years/${itemKey.split('+')[2]}`,
        getRefreshDataHeader(refreshData),
      );
      const responseData = response.data;
      responseData.dataType = 'Integer';
      return [responseData];
    } else if (category.toLocaleLowerCase() === 'analysis') {
      const responseData = {
        id: 1,
        name: 'average',
        dataType: 'Integer',
        hasLookups: false,
        lookupValues: [],
        year: itemKey.split('+')[2],
      } as any;
      return [responseData];
    } else if (category.toLocaleLowerCase() === 'analysis schemes') {
      const response = await get<string[]>(
        `visualiser/rules/attributes/schemes/parameters/${parameterId}/treatments/years/${itemKey.split('+')[2]}`,
      );
      const responseData = {
        id: 1,
        name: 'treatment',
        dataType: 'String',
        hasLookups: true,
        lookupValues: response.data,
      } as any;
      return responseData;
    } else if (category.toLocaleLowerCase() === 'candidate schemes') {
      const response = await get<string[]>(
        `visualiser/rules/attributes/schemes/fwps/years/${itemKey.split('+')[2]}`,
        getRefreshDataHeader(refreshData),
      );
      const responseData = {
        id: 1,
        name: 'treatment',
        dataType: 'String',
        hasLookups: true,
        lookupValues: response.data,
      } as any;
      return responseData;
    } else if (category.toLocaleLowerCase() === 'programmed schemes') {
      const treatmentsResponse = await get<string[]>(
        `visualiser/rules/attributes/schemes/programmed/${parameterId}/years/${itemKey.split('+')[2]}/treatments`,
      );

      const statusResponse = await get<string[]>(
        `visualiser/rules/attributes/schemes/programmed/${parameterId}/years/${itemKey.split('+')[2]}/status`,
      );

      const responseData = [
        {
          id: 1,
          name: 'cost',
          dataType: 'Integer',
          hasLookups: false,
          lookupValues: [],
          year: itemKey.split('+')[2],
        },
        {
          id: 2,
          name: 'treatment',
          dataType: 'String',
          hasLookups: true,
          lookupValues: treatmentsResponse.data,
        },
        {
          id: 3,
          name: 'status',
          dataType: 'String',
          hasLookups: true,
          lookupValues: statusResponse.data,
        },
      ] as any;
      return responseData;
    } else {
      const response = await get<AssetTypeAttributesInfo[]>(
        `visualiser/rules/attributes/assets/${type}`,
        getRefreshDataHeader(refreshData),
      );
      const responseData = response.data;
      return responseData;
    }
  };

  const getAssetTypeRange = async (
    category: string,
    id: number,
    parameterId?: number,
  ): Promise<AssetElementsRangeInfo> => {
    if (category.toLocaleLowerCase() === 'performance') {
      const response = await get<AssetElementsRangeInfo>(
        `visualiser/rules/attributes/performance/attribute/${id}/range`,
      );
      const responseData = response.data;
      return responseData;
    } else if (category.toLocaleLowerCase() === 'analysis') {
      const response = await get<AssetElementsRangeInfo>(
        `visualiser/rules/attributes/schemes/parameters/${parameterId}/years/${id}`,
      );
      const responseData = response.data;
      return responseData;
    } else if (category.toLocaleLowerCase() === 'programmed schemes') {
      const response = await get<AssetElementsRangeInfo>(
        `visualiser/rules/attributes/schemes/programmed/${parameterId}/years/${id}/costs`,
      );
      const responseData = response.data;
      return responseData;
    } else {
      const response = await get<AssetElementsRangeInfo>(
        `visualiser/rules/attributes/assets/attribute/${id}/range`,
      );
      const responseData = response.data;
      return responseData;
    }
  };

  const getGraphData = async (
    id: number,
    requestType: string,
    refreshData: boolean = false,
  ): Promise<StatisticsResponse> => {
    const response = await get<StatisticsResponse>(
      `stats/assets/${id}/${requestType}`,
      getRefreshDataHeader(refreshData),
    );
    const responseData = response.data;
    return responseData;
  };

  const getInspectionGraphData = async (
    refreshData: boolean = false,
  ): Promise<StatisticsResponse> => {
    const response = await get<StatisticsResponse>(`stats/inspections/new`, {
      ...getRefreshDataHeader(refreshData),
    });
    const responseData = response.data;
    return responseData;
  };

  const getElementAttributes = async (
    id: number,
    spanNumber: number,
    elementId: number,
    refreshData: boolean = false,
  ): Promise<AssetAttributesInfo[]> => {
    const response = await get<AssetAttributesInfo[]>(
      `assets/${id}/spans/${spanNumber}/elements/${elementId}/attributes`,
      getRefreshDataHeader(refreshData),
    );
    const responseData = response.data;
    return responseData;
  };

  const getSpanInfo = async (
    id: number,
    spanNumber: number,
  ): Promise<any[]> => {
    const response = await get<any>(`assets/${id}/spans/${spanNumber}`);
    const responseData = response.data;
    return responseData;
  };

  return {
    getSpans,
    getEyeViYears,
    getAssetDetails,
    getAssetId,
    getAssetInspectionDetails,
    getInspectionRecords,
    getInspectionReport,
    getAssetElements,
    getAssetTypeRange,
    getAssetAttributes,
    getAssetPerformanceIndicator,
    getEyeViCredentials,
    getNetworkAttributes,
    getAssetTypeAttributes,
    getGraphData,
    getInspectionGraphData,
    getElementAttributes,
    getSpanInfo,
  };
};

export { assetActions };
